<template>
    <div class="atm-page">
        <!-- Attendance Settings Card -->
        <div class="atm-card">
            <div class="atm-card__header">
                <div class="atm-card__title">
                    <i class="fas fa-clock"></i>
                    Attendance Settings
                </div>
            </div>
            <div class="atm-card__body">
                <div class="atm-grid">
                    <!-- Select Shift -->
                    <div class="atm-field atm-field--wide">
                        <label class="atm-label">
                            <i class="fas fa-business-time"></i> Shift Assignment
                        </label>
                        <SelectDropdown
                            v-if="attendanceShifts && attendanceShifts.length"
                            :multiple="true"
                            id="user-selection"
                            @change="handleAttendanceShiftIdsChange"
                            :value="attendanceShiftIds"
                            :options="
                                attendanceShifts.map((attendanceShift) => ({
                                    id: attendanceShift.id,
                                    title: `${attendanceShift.name} (${attendanceShift.startTime} - ${attendanceShift.endTime})`,
                                }))
                            "
                        />
                    </div>

                    <!-- Geolocation Toggle -->
                    <div class="atm-field">
                        <label class="atm-label">
                            <i class="fas fa-map-marker-alt"></i> Geolocation Tracking
                        </label>
                        <div class="atm-toggle-row">
                            <label class="atm-switch">
                                <input v-model="trackGeolocation" type="checkbox" />
                                <span class="atm-switch__slider"></span>
                            </label>
                            <span class="atm-toggle-text">{{ trackGeolocation ? 'Enabled' : 'Disabled' }}</span>
                        </div>
                    </div>

                    <!-- Offline Login Toggle -->
                    <div class="atm-field">
                        <label class="atm-label">
                            <i class="fas fa-wifi"></i> Offline Login / Check In
                        </label>
                        <div class="atm-toggle-row">
                            <label class="atm-switch">
                                <input v-model="allowOfflineLogin" type="checkbox" />
                                <span class="atm-switch__slider"></span>
                            </label>
                            <span class="atm-toggle-text">{{ allowOfflineLogin ? 'Allowed' : 'Not Allowed' }}</span>
                        </div>
                        <div class="atm-device-info" v-if="registeredDeviceId">
                            <div class="atm-device-badge">
                                <i class="fas fa-mobile-alt"></i>
                                <div>
                                    <span class="atm-device-label">Registered Device</span>
                                    <strong class="atm-device-id">{{ registeredDeviceId }}</strong>
                                </div>
                            </div>
                            <button class="atm-btn atm-btn--danger-sm" @click="deregisterDeviceId" :disabled="deregisteringDevice">
                                <i class="fas fa-unlink"></i>
                                {{ deregisteringDevice ? "Wait..." : "Deregister" }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="atm-card__footer">
                    <button class="atm-btn atm-btn--primary" @click="updateEmployeeAttendanceDetails" :disabled="updating">
                        <i class="fas fa-save" v-if="!updating"></i>
                        <i class="fas fa-spinner fa-spin" v-else></i>
                        {{ updating ? "Saving..." : "Update Attendance Details" }}
                    </button>
                </div>
            </div>
        </div>

        <AttendanceDateController :stats="stats" @update="fetchEmployeeAttendanceRecords" :employeeMode="true" :monthMode="true" />
        <div>
            <AttendanceRecordListItem
                :employeeMode="true"
                :attendanceRecord="attendanceRecord"
                :key="attendanceRecord.id"
                v-for="attendanceRecord of employeeAttendanceRecords"
                @updated="refetchData"
            />
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import { mapState, mapActions } from "vuex"
import AttendanceRecordListItem from "../attendance/attendance-records/attendance-record-list-item.vue"
import AttendanceDateController from "../attendance/attendance-records/attendance-date-controller.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import Button from "../../components/UI/Button.vue"
import SectionTitle from "../../components/UI/SectionTitle.vue"
import SelectDropdown from "../../components/crud/SelectDropdown.vue"

export default {
    components: {
        SectionTitle,
        AttendanceRecordListItem,
        AttendanceDateController,
        CustomInput,
        Button,
        SelectDropdown,
    },
    props: {
        employeeId: {
            type: [Number, String],
            default: "",
        },
    },
    data() {
        return {
            employeeAttendanceRecords: [],
            stats: null,
            overtimeRate: "",
            attendanceShiftIds: [],
            updating: false,
            trackGeolocation: false,
            editEmployeeData: null,
            allowOfflineLogin: false,
            registeredDeviceId: "",
            deregisteringDevice: false,
        }
    },
    computed: {
        ...mapState("misc", ["attendanceDate", "attendanceShifts", "attendanceStatusId"]),
    },
    async mounted() {
        await this.fetchEmployee()
        // await this.fetchAttendanceInfo()
        await this.fetchEmployeeAttendanceRecords()
    },
    methods: {
        ...mapActions("misc", ["fetchAttendanceInfo"]),
        async deregisterDeviceId() {
            if (this.deregisteringDevice) {
                return
            }
            if (!confirm("Are you sure to deregister employee? Employee will have to register again for offline logins")) {
                return
            }
            try {
                this.deregisteringDevice = true
                const res = await http.patch(`/employees/${this.employeeId}/deregister-devices`)
                if (res.data.result) {
                    this.registeredDeviceId = ""
                    this.$toast.success("Device Deregistered Successfully!")
                }
            } catch (err) {
                this.$toast.success("Device Deregistered Successfully!")
                console.log("error in deregistering device", err)
            }
            this.deregisteringDevice = false
        },
        handleAttendanceShiftIdsChange(vals) {
            this.attendanceShiftIds = vals
        },
        async fetchEmployeeAttendanceRecords() {
            const res = await http.get(
                `/attendance-records/filter?allow_role_condition=false&date=${this.attendanceDate}&search_date=false&user_id=${this.employeeId}&status_id=${this.attendanceStatusId}&stats=true`
            )
            this.employeeAttendanceRecords = res.data.result
            this.stats = res.data.stats
        },
        refetchData() {
            console.log("refetch data")
            this.fetchEmployeeAttendanceRecords()
        },
        async fetchEmployee() {
            const res = await http.get("/users/" + this.employeeId)
            this.editEmployeeData = res.data.result
            this.attendanceShiftIds = this.editEmployeeData.attendanceShiftIds ? this.editEmployeeData.attendanceShiftIds.split(",") : []
            this.overtimeRate = this.editEmployeeData.overtimeRate
            this.trackGeolocation = this.editEmployeeData.trackGeolocation
            this.allowOfflineLogin = this.editEmployeeData.allowOfflineLogin
            this.registeredDeviceId = this.editEmployeeData.deviceId
        },
        async updateEmployeeAttendanceDetails() {
            if (this.updating) {
                return
            }
            this.updating = true
            try {
                const formData = new FormData()
                formData.append("overtimeRate", Number(this.overtimeRate || 0) || "")
                formData.append("attendanceShiftIds", this.attendanceShiftIds.join(",") || "")
                formData.append("trackGeolocation", this.trackGeolocation || "")
                formData.append("allowOfflineLogin", this.allowOfflineLogin || "")

                await http.patch(`/employees/${this.employeeId}`, formData)

                this.$toast.success("Employee's Attendance Details updated")
            } catch (err) {
                console.log("error in updating attendance dettails", err)
                this.$toast.error(err)
            }
            this.updating = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.atm-page {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

/* ── Card ── */
.atm-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusMd;
    box-shadow: $shadowSm;
    overflow: hidden;
}
.atm-card__header {
    padding: 16px 20px;
    border-bottom: 1px solid $gray100;
    background: $gray50;
}
.atm-card__title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    display: flex;
    align-items: center;
    gap: 8px;

    i { color: $primaryColor; font-size: 14px; }
}
.atm-card__body {
    padding: 20px;
}
.atm-card__footer {
    padding-top: 18px;
    margin-top: 8px;
    border-top: 1px solid $gray100;
}

/* ── Grid ── */
.atm-grid {
    display: grid;
    grid-template-columns: 1.2fr 1fr 1fr;
    gap: 20px;
    align-items: start;
}

/* ── Field ── */
.atm-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.atm-field--wide {
    // takes more room already via grid
}
.atm-label {
    font-size: 11px;
    font-weight: 600;
    color: $gray500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 2px;

    i { font-size: 10px; color: $gray400; }
}

/* ── Toggle Switch ── */
.atm-toggle-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 0 2px;
}
.atm-toggle-text {
    font-size: 13px;
    color: $gray600;
    font-weight: 500;
}
.atm-switch {
    position: relative;
    display: inline-block;
    width: 42px;
    height: 24px;
    flex-shrink: 0;

    input {
        opacity: 0;
        width: 0;
        height: 0;

        &:checked + .atm-switch__slider {
            background: $primaryColor;
        }
        &:checked + .atm-switch__slider::before {
            transform: translateX(18px);
        }
    }
}
.atm-switch__slider {
    position: absolute;
    cursor: pointer;
    inset: 0;
    background: $gray300;
    border-radius: 24px;
    transition: background $transitionFast;

    &::before {
        content: '';
        position: absolute;
        height: 18px;
        width: 18px;
        left: 3px;
        bottom: 3px;
        background: white;
        border-radius: 50%;
        transition: transform $transitionFast;
        box-shadow: 0 1px 3px rgba(0,0,0,0.15);
    }
}

/* ── Device Info ── */
.atm-device-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 10px;
    padding: 10px 12px;
    background: $gray50;
    border: 1px solid $gray100;
    border-radius: $radiusSm;
}
.atm-device-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: $gray600;

    i {
        font-size: 16px;
        color: $primaryColor;
    }
}
.atm-device-label {
    display: block;
    font-size: 10px;
    color: $gray400;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}
.atm-device-id {
    font-size: 12px;
    color: $gray700;
    font-weight: 600;
    word-break: break-all;
}

/* ── Buttons ── */
.atm-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 9px 20px;
    border-radius: $radiusSm;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    border: none;
    transition: all $transitionFast;

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }

    &--primary {
        background: $primaryColor;
        color: white;

        &:hover:not(:disabled) { background: $primaryDark; }
    }
    &--danger-sm {
        background: white;
        color: $danger;
        border: 1px solid lighten($danger, 20%);
        padding: 5px 12px;
        font-size: 11px;
        border-radius: 6px;

        &:hover:not(:disabled) {
            background: lighten($danger, 30%);
        }
    }
}

/* ── Responsive ── */
@media (max-width: 992px) {
    .atm-grid {
        grid-template-columns: 1fr 1fr;
    }
}
@media (max-width: 640px) {
    .atm-grid {
        grid-template-columns: 1fr;
    }
    .atm-card__body { padding: 14px; }
    .atm-device-info { flex-direction: column; align-items: flex-start; }
}
</style>
